import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Briefcase, ArrowRight, Check, Sparkles, Calendar as CalendarIcon, Clock, ListChecks } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const Meetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [nextSteps, setNextSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeeting, setShowMeeting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/meetings"), api.get("/next-steps")])
      .then(([m, n]) => {
        setMeetings(m.data);
        setNextSteps(n.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const todayIso = new Date().toISOString().slice(0, 10);
  const upcomingMeetings = meetings
    .filter((m) => (m.date || "").slice(0, 10) >= todayIso && m.status !== "cancelada")
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .slice(0, 5);

  const pendingSteps = nextSteps.filter((s) => s.status !== "completado").slice(0, 8);

  const generateDailySummary = async () => {
    setGeneratingSummary(true);
    try {
      const meetingsLine = upcomingMeetings.map((m) => `- ${new Date(m.date).toLocaleString("es-MX")} · ${m.title}${m.objective ? ` (objetivo: ${m.objective})` : ""}`).join("\n") || "(sin reuniones)";
      const stepsLine = pendingSteps.map((s) => `- ${s.description} · responsable: ${s.responsible_name || "—"} · entrega: ${s.due_date || "—"} · prioridad ${s.priority}`).join("\n") || "(sin pendientes)";
      const prompt = `Eres el copiloto estratégico diario del CEO de Rubí Perfumería. Hoy es ${new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}.

PRÓXIMAS REUNIONES:
${meetingsLine}

PRÓXIMOS PASOS PENDIENTES:
${stepsLine}

Devuelve en MARKDOWN, máximo 6 líneas:
**🎯 Foco del día**: 1 línea
**⚡ 3 acciones críticas hoy**: bullets cortos
**⚠️ Riesgos**: 1 línea
**💡 Sugerencia estratégica de Aura**: 1 línea`;
      const res = await api.post("/aura/generate", { prompt, task_type: "executive" });
      setAiSummary(res.data.output);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setGeneratingSummary(false);
    }
  };

  const tabs = ["meetings", "steps"];
  const [tab, setTab] = useState("meetings");

  return (
    <Layout
      title="Reuniones & Próximos Pasos"
      subtitle="Sincronizado con Google Calendar · panel diario con Aura"
      actions={
        <button onClick={() => setShowMeeting(true)} className="btn-primary flex items-center gap-2" data-testid="new-meeting-btn">
          <Plus size={14} /> Nueva reunión
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        {/* Main column */}
        <div>
          <div className="flex gap-2 mb-5 border-b border-[#E5E5E5]">
            <button onClick={() => setTab("meetings")} data-testid="tab-meetings" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "meetings" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
              Reuniones ({meetings.length})
            </button>
            <button onClick={() => setTab("steps")} data-testid="tab-steps" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "steps" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
              Próximos pasos ({nextSteps.filter((s) => s.status !== "completado").length})
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><span className="spinner" /></div>
          ) : tab === "meetings" ? (
            meetings.length === 0 ? (
              <EmptyMeetings onCreate={() => setShowMeeting(true)} />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {meetings.map((m) => (
                  <div key={m.id} className="aura-card p-5 cursor-pointer" onClick={() => setSelected(m)} data-testid={`meeting-${m.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="label-overline">{new Date(m.date).toLocaleDateString("es-MX", { day: "numeric", month: "long" })}</div>
                        <h3 className="font-display text-lg font-medium mt-1">{m.title}</h3>
                      </div>
                      <span className={`pill ${m.status === "programada" ? "pill-blue" : m.status === "terminada" ? "pill-green" : "pill-gray"}`}>{m.status}</span>
                    </div>
                    {m.objective && <p className="text-sm text-[#666] line-clamp-2">{m.objective}</p>}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {(m.participants || []).slice(0, 5).map((p) => (
                        <span key={p} className="pill pill-gray text-[10px]">{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <NextStepsPanel items={nextSteps} meetings={meetings} onChange={load} />
          )}
        </div>

        {/* Side panel - daily check */}
        <aside className="space-y-4" data-testid="daily-check-panel">
          <div className="aura-card-flat p-5 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/20">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <div className="aura-logo-mark w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold">A</div>
                <div>
                  <div className="label-overline">Check diario</div>
                  <div className="font-display text-base font-medium">Resumen de Aura</div>
                </div>
              </div>
              <button onClick={generateDailySummary} disabled={generatingSummary} className="btn-outline text-xs flex items-center gap-1" data-testid="generate-summary-btn">
                {generatingSummary ? <span className="spinner" /> : <Sparkles size={12} />} {aiSummary ? "Regenerar" : "Generar"}
              </button>
            </div>
            {aiSummary ? (
              <div className="text-xs markdown-content max-h-72 overflow-y-auto" data-testid="ai-summary">
                <Markdown content={aiSummary} />
              </div>
            ) : (
              <p className="text-xs text-[#666]">Aura analizará reuniones y pendientes y te dará foco del día con 3 acciones críticas.</p>
            )}
          </div>

          {/* Upcoming meetings mini list */}
          <div className="aura-card-flat p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon size={14} className="text-[#8a5e10]" />
              <div className="font-display text-sm font-medium">Próximas reuniones</div>
            </div>
            {upcomingMeetings.length === 0 ? (
              <div className="text-xs text-[#666]">Sin reuniones próximas.</div>
            ) : (
              <div className="space-y-2">
                {upcomingMeetings.map((m) => (
                  <div key={m.id} onClick={() => setSelected(m)} className="text-xs cursor-pointer hover:bg-[#FAFAFA] rounded p-2 -mx-2" data-testid={`upcoming-meeting-${m.id}`}>
                    <div className="font-medium">{m.title}</div>
                    <div className="text-[#666] flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {new Date(m.date).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending steps mini list */}
          <div className="aura-card-flat p-5">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks size={14} className="text-[#8a5e10]" />
              <div className="font-display text-sm font-medium">Pendientes activos</div>
            </div>
            {pendingSteps.length === 0 ? (
              <div className="text-xs text-[#666]">Sin pendientes.</div>
            ) : (
              <div className="space-y-2">
                {pendingSteps.map((s) => (
                  <div key={s.id} className="text-xs" data-testid={`pending-step-${s.id}`}>
                    <div className="font-medium">{s.description}</div>
                    <div className="text-[#666] mt-0.5">
                      {s.responsible_name || "—"}{s.due_date && ` · ${new Date(s.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}`} · <span className={s.priority === "alta" ? "text-red-600" : "text-[#666]"}>{s.priority}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {showMeeting && <MeetingForm onClose={() => setShowMeeting(false)} onSaved={() => { setShowMeeting(false); load(); }} />}
      {selected && <MeetingDetail meeting={selected} onClose={() => setSelected(null)} onChange={() => { setSelected(null); load(); }} />}
    </Layout>
  );
};

const EmptyMeetings = ({ onCreate }) => (
  <div className="aura-card-flat p-12 text-center">
    <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
      <Briefcase size={24} />
    </div>
    <h3 className="font-display text-2xl font-medium">Sin reuniones</h3>
    <p className="text-[#666] mt-2 mb-5">Crea reuniones y convierte acuerdos en próximos pasos accionables.</p>
    <button onClick={onCreate} className="btn-primary">Crear primera reunión</button>
  </div>
);

const NextStepsPanel = ({ items, meetings, onChange }) => {
  const [showForm, setShowForm] = useState(false);
  const toggle = async (id, status) => {
    await api.patch(`/next-steps/${id}`, { status });
    onChange();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/next-steps/${id}`);
    onChange();
  };
  return (
    <div>
      <button onClick={() => setShowForm(true)} className="btn-outline mb-4 flex items-center gap-2" data-testid="new-step-btn">
        <Plus size={14} /> Nuevo próximo paso
      </button>
      {items.length === 0 ? (
        <div className="text-center py-12 aura-card-flat text-sm text-[#666]">Sin próximos pasos. Crea uno o conviértelos desde reuniones.</div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.id} className={`aura-card-flat p-4 flex items-center gap-3 ${s.status === "completado" ? "opacity-60" : ""}`} data-testid={`step-${s.id}`}>
              <button
                onClick={() => toggle(s.id, s.status === "completado" ? "pendiente" : "completado")}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition ${s.status === "completado" ? "bg-[#D99D26] border-[#D99D26]" : "border-[#E5E5E5] hover:border-[#D99D26]"}`}
              >
                {s.status === "completado" && <Check size={14} className="text-[#0A0A0A]" />}
              </button>
              <div className="flex-1">
                <div className={`text-sm font-medium ${s.status === "completado" ? "line-through" : ""}`}>{s.description}</div>
                <div className="text-xs text-[#666] mt-1 flex gap-3">
                  {s.responsible_name && <span>{s.responsible_name}</span>}
                  {s.due_date && <span>· Entrega: {new Date(s.due_date).toLocaleDateString("es-MX")}</span>}
                  <span className={`pill ${s.priority === "alta" ? "pill-red" : s.priority === "media" ? "pill-yellow" : "pill-gray"} text-[9px]`}>{s.priority}</span>
                </div>
              </div>
              <button onClick={() => remove(s.id)} className="text-[#666] hover:text-red-600"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
      {showForm && <StepForm meetings={meetings} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); onChange(); }} />}
    </div>
  );
};

const StepForm = ({ meetings, onClose, onSaved }) => {
  const [form, setForm] = useState({ description: "", meeting_id: "", responsible_name: "", due_date: "", priority: "media", status: "pendiente" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    await api.post("/next-steps", { ...form, due_date: form.due_date || null });
    toast.success("Creado");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div><div className="label-overline">Crear</div><h2 className="font-display text-2xl font-medium">Próximo paso</h2></div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div><label className="aura-label">Descripción *</label><input required className="aura-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aura-label">Responsable</label><input className="aura-input" value={form.responsible_name} onChange={(e) => setForm({ ...form, responsible_name: e.target.value })} /></div>
          <div><label className="aura-label">Fecha límite</label><input type="date" className="aura-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          <div><label className="aura-label">Prioridad</label><select className="aura-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option></select></div>
          <div><label className="aura-label">Reunión</label><select className="aura-input" value={form.meeting_id} onChange={(e) => setForm({ ...form, meeting_id: e.target.value })}><option value="">— Ninguna —</option>{meetings.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}</select></div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center">Crear</button>
      </form>
    </div>
  );
};

const MeetingForm = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: "", date: new Date().toISOString().slice(0, 16), duration_minutes: 60,
    participants: "", area: "marketing", objective: "", agenda: "", document_link: "", meet_link: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const body = {
      ...form,
      duration_minutes: parseInt(form.duration_minutes) || 60,
      participants: form.participants.split(",").map((p) => p.trim()).filter(Boolean),
    };
    await api.post("/meetings", body);
    toast.success("Reunión creada");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-7 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start"><div><div className="label-overline">Crear</div><h2 className="font-display text-2xl font-medium">Nueva reunión</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div><label className="aura-label">Título *</label><input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-title" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aura-label">Fecha y hora</label><input type="datetime-local" className="aura-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><label className="aura-label">Duración (min)</label><input type="number" className="aura-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
        </div>
        <div><label className="aura-label">Participantes (separados por coma)</label><input className="aura-input" value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} placeholder="Benjamín, Director, Tere..." /></div>
        <div><label className="aura-label">Objetivo</label><input className="aura-input" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} /></div>
        <div><label className="aura-label">Agenda</label><textarea className="aura-input" rows="3" value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aura-label">Link Google Doc</label><input className="aura-input" value={form.document_link} onChange={(e) => setForm({ ...form, document_link: e.target.value })} placeholder="https://docs.google.com/..." /></div>
          <div><label className="aura-label">Link Meet</label><input className="aura-input" value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/..." /></div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center" data-testid="meeting-submit">Crear reunión</button>
      </form>
    </div>
  );
};

const MeetingDetail = ({ meeting, onClose, onChange }) => {
  const [notes, setNotes] = useState(meeting.notes || "");
  const [status, setStatus] = useState(meeting.status);
  const [creating, setCreating] = useState(false);

  const save = async () => {
    await api.patch(`/meetings/${meeting.id}`, { notes, status });
    toast.success("Guardado");
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar reunión?")) return;
    await api.delete(`/meetings/${meeting.id}`);
    onChange();
  };

  const convertToSteps = async () => {
    if (!notes.trim()) {
      toast.error("Agrega notas o resumen primero");
      return;
    }
    setCreating(true);
    try {
      // Use Aura to extract action items
      const prompt = `Extrae los próximos pasos accionables (con responsable si se menciona, fecha si se menciona, prioridad si se infiere) de estas notas de reunión. Devuélvelos como lista markdown clara:\n\n${notes}`;
      const res = await api.post("/aura/generate", { prompt, task_type: "executive" });
      const output = res.data.output;
      // Parse list items - very simple, extract lines starting with - or *
      const steps = output.split("\n").filter((l) => /^[-*]\s/.test(l)).map((l) => l.replace(/^[-*]\s/, "").trim());
      for (const desc of steps.slice(0, 20)) {
        await api.post("/next-steps", {
          description: desc.substring(0, 300),
          meeting_id: meeting.id,
          status: "pendiente",
          priority: "media",
        });
      }
      toast.success(`${steps.length} próximos pasos creados`);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">{new Date(meeting.date).toLocaleString("es-MX")}</div>
            <h2 className="font-display text-2xl font-medium">{meeting.title}</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {["programada", "en_curso", "terminada", "cancelada"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`pill cursor-pointer ${status === s ? "pill-yellow" : "pill-gray"} text-[10px]`}>{s}</button>
          ))}
        </div>
        {meeting.objective && <div className="mb-3"><div className="label-overline">Objetivo</div><div className="text-sm mt-1">{meeting.objective}</div></div>}
        {meeting.agenda && <div className="mb-3"><div className="label-overline">Agenda</div><pre className="text-sm whitespace-pre-wrap mt-1 font-sans">{meeting.agenda}</pre></div>}
        <div className="flex gap-2 mb-4">
          {meeting.meet_link && <a href={meeting.meet_link} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs">Abrir Meet</a>}
          {meeting.document_link && <a href={meeting.document_link} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs">Abrir Doc</a>}
        </div>
        <div>
          <div className="label-overline mb-2">Notas / resumen / acuerdos</div>
          <textarea className="aura-input" rows="6" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Acuerdos: ... \nPróximos pasos: ..." data-testid="meeting-notes" />
        </div>
        <div className="flex justify-between mt-4">
          <button onClick={remove} className="btn-ghost text-red-600"><Trash2 size={14} className="inline mr-1" /> Eliminar</button>
          <div className="flex gap-2">
            <button onClick={convertToSteps} disabled={creating} className="btn-outline flex items-center gap-2" data-testid="convert-steps-btn">
              {creating ? <span className="spinner" /> : <ArrowRight size={14} />} Aura → próximos pasos
            </button>
            <button onClick={save} className="btn-primary">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Meetings;
